<!--
  TranslatePopup · 选词翻译弹窗
  props: visible (Boolean), text (String), x (Number), y (Number)
  emit:  close
  - 接收选中文本和鼠标坐标，调用 /api/translate 获取翻译
  - 单词显示音标+词性+释义，短句显示中文翻译
-->
<template>
  <div v-if="visible" class="tr-popup" :style="popupStyle" @click.stop @mouseup.stop>
    <!-- 关闭按钮 -->
    <button class="tr-close" @click="$emit('close')">×</button>

    <!-- 加载中 -->
    <div v-if="loading" class="tr-loading">
      <span class="tr-spinner"></span> 正在翻译…
    </div>

    <template v-else-if="result">
      <!-- 单词/短语：词典头部 -->
      <div v-if="result.is_phrase && result.dictionary" class="tr-head">
        <span class="tr-word">{{ result.dictionary.word }}</span>
        <span v-if="result.dictionary.phonetic" class="tr-phonetic">{{ result.dictionary.phonetic }}</span>
      </div>

      <!-- 翻译结果 -->
      <div v-if="result.translation" class="tr-translation">
        <span v-if="!result.is_phrase || !result.dictionary" class="tr-label">译文</span>
        {{ result.translation }}
      </div>

      <!-- 词典释义 -->
      <div v-if="result.dictionary && result.dictionary.meanings.length" class="tr-dict">
        <div v-for="(m, mi) in result.dictionary.meanings" :key="mi" class="tr-meaning">
          <span class="tr-pos">{{ m.part_of_speech }}</span>
          <div v-for="(d, di) in m.definitions" :key="di" class="tr-def">
            <span class="tr-def-text">{{ d.definition }}</span>
            <span v-if="d.example" class="tr-def-example">"{{ d.example }}"</span>
          </div>
        </div>
      </div>

      <!-- 错误提示 -->
      <div v-if="result.error && !result.translation" class="tr-error">
        {{ result.error }}
      </div>
    </template>

    <!-- 空状态 -->
    <div v-else class="tr-error">翻译失败，请重试</div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'

const props = defineProps({
  visible: { type: Boolean, default: false },
  text: { type: String, default: '' },
  x: { type: Number, default: 0 },
  y: { type: Number, default: 0 },
})
defineEmits(['close'])

const loading = ref(false)
const result = ref(null)

// 弹窗定位：基于鼠标坐标，自动避免超出视口右/下边界
const popupStyle = computed(() => {
  const w = 360, h = 320  // 预估弹窗宽高
  let left = props.x + 12
  let top = props.y + 12
  if (left + w > window.innerWidth) left = props.x - w - 12
  if (top + h > window.innerHeight) top = props.y - h - 12
  if (left < 8) left = 8
  if (top < 8) top = 8
  return { left: left + 'px', top: top + 'px' }
})

// 监听 visible / text 变化时触发翻译
watch(() => [props.visible, props.text], async ([vis, txt]) => {
  if (!vis || !txt) return
  loading.value = true
  result.value = null
  try {
    const resp = await fetch('/api/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: txt }),
    })
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    result.value = await resp.json()
  } catch (e) {
    result.value = { text: txt, translation: '', error: `请求失败: ${e.message}`, dictionary: null }
  } finally {
    loading.value = false
  }
}, { immediate: true })
</script>

<style scoped>
.tr-popup {
  position: fixed;
  z-index: 9999;
  width: 340px;
  max-height: 380px;
  overflow-y: auto;
  background: var(--panel, #161e3a);
  border: 1px solid var(--accent, #6ea8ff);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  padding: 14px 16px 16px;
  font-size: 13px;
  line-height: 1.5;
  color: #e0e6f0;
  backdrop-filter: blur(16px);
}

.tr-close {
  position: absolute;
  top: 8px;
  right: 10px;
  background: none;
  border: none;
  color: var(--mute, #6b7a99);
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
}
.tr-close:hover { color: #fff; background: rgba(255,255,255,0.1); }

.tr-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: var(--mute, #6b7a99);
}
.tr-spinner {
  width: 14px; height: 14px;
  border: 2px solid var(--mute, #6b7a99);
  border-top-color: var(--accent, #6ea8ff);
  border-radius: 50%;
  animation: tr-spin 0.6s linear infinite;
}
@keyframes tr-spin { to { transform: rotate(360deg); } }

.tr-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
  padding-right: 24px;
}
.tr-word {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}
.tr-phonetic {
  font-size: 13px;
  color: var(--accent, #6ea8ff);
  font-style: italic;
}

.tr-translation {
  padding: 6px 0;
  border-top: 1px solid var(--line, #2a3550);
  border-bottom: 1px solid var(--line, #2a3550);
  margin: 6px 0;
  color: #c8d4e8;
}
.tr-label {
  display: inline-block;
  font-size: 11px;
  color: var(--mute, #6b7a99);
  margin-right: 6px;
  vertical-align: top;
}

.tr-dict { margin-top: 4px; }
.tr-meaning { margin-bottom: 8px; }
.tr-meaning:last-child { margin-bottom: 0; }
.tr-pos {
  display: inline-block;
  font-size: 11px;
  font-style: italic;
  color: var(--accent, #6ea8ff);
  background: rgba(110, 168, 255, 0.1);
  padding: 1px 6px;
  border-radius: 4px;
  margin-bottom: 4px;
}
.tr-def { margin: 2px 0 2px 12px; }
.tr-def-text { color: #d0d8e8; }
.tr-def-example {
  display: block;
  font-size: 12px;
  color: var(--mute, #6b7a99);
  font-style: italic;
  margin-top: 1px;
}

.tr-error {
  padding: 8px 0;
  color: var(--bad, #ff6b6b);
  font-size: 12px;
}
</style>
